<template>
  <el-dialog :visible.sync="isVisible" :close-on-click-modal="false">
    <div class="one-page" ref="print">
      <div class="mast-head">
        <div class="colony-logo">
          <div class="logo-wrapper">
            <img :src="colonyLogoB64" />
          </div>
        </div>
        <div class="company-logo" v-if="company.logo">
          <div class="logo-wrapper">
            <img :src="companyLogoB64" />
          </div>
        </div>
        <div class="clearfix"></div>
      </div>
      <div>
        <h1>{{ currentTitle }}</h1>
        <div class="monitor pull-right" v-if="!isEditingTitle">
          <el-tooltip class="item" content="Edit title" placement="right">
            <el-button type="text" @click="isEditingTitle = true">
              <i class="fa fa-fw fa-edit"></i>
            </el-button>
          </el-tooltip>
        </div>
        <div v-else>
          <el-input v-model="currentTitle"></el-input>
          <div class="edit-buttons">
            <el-button-group>
              <el-button type="danger" size="mini" @click="handleResetTitle">Reset</el-button>
              <el-button type="primary" size="mini" @click="handleSaveTitle">Save</el-button>
            </el-button-group>
          </div>
        </div>
      </div>
      <div class="clearfix"></div>
      <!-- start of insight -->
      <div class="insight" v-if="statData.insight">
        <div v-html="processedInsight"></div>
        <div class="monitor pull-right" v-if="!isEditingInsight">
          <el-tooltip class="item" content="Edit insight" placement="right">
            <el-button type="text" @click="isEditingInsight = true">
              <i class="fa fa-fw fa-edit"></i>
            </el-button>
          </el-tooltip>
        </div>
        <div v-else>
          <div class="monitor">
            <span>
              <el-checkbox v-model="positiveSelections.main"></el-checkbox>
            </span>
            <textarea v-model="insight" ref="insightTextArea"></textarea>
            <div class="edit-buttons">
              <el-button-group>
                <el-button
                  size="mini"
                  v-for="item in insightVariables"
                  :key="item.key"
                  @click="handleAppendInsightVariable('insightTextArea', item.key)"
                  >{{ item.title }}</el-button
                >
                <el-button type="danger" size="mini" @click="handleResetInsight">Reset</el-button>
                <el-button
                  v-if="isEditingInsight"
                  type="primary"
                  size="mini"
                  @click="handleSaveInsight"
                  >Save</el-button
                >
              </el-button-group>
            </div>
          </div>
        </div>
        <div class="monitor clearfix"></div>
      </div>
      <!-- end of insight  -->

      <div class="insight-chart">
        <div
          class="pull-right positive-only monitor"
          v-if="statData.chart !== 'VERTICAL_BAR_STACKED'"
        >
          <strong style="positive-only-check-box">Display only positive</strong>
          <el-checkbox v-model="positiveSelections.main"></el-checkbox>
        </div>
        <div class="clearfix monitor"></div>
        <div>
          <bar-chart
            :height="200"
            :data="mainChartTotalDisplay(statData.answers)"
            :printable="printCharts"
            :tooltipsEnabled="!printCharts"
            :animationDuration="1"
            v-if="statData.chart.length === 0 || statData.chart === 'VERTICAL_BAR'"
          />
          <horizontal-bar-chart
            :height="200"
            :data="mainChartTotalDisplay(statData.answers)"
            :printable="printCharts"
            :tooltipsEnabled="!printCharts"
            :animationDuration="1"
            v-if="statData.chart === 'HORIZONTAL_BAR'"
          />
          <pie-chart
            :height="200"
            :data="mainChartTotalDisplay(statData.answers)"
            :printable="printCharts"
            :tooltipsEnabled="!printCharts"
            :animationDuration="1"
            v-if="statData.chart === 'PIE'"
          />
          <doughnut-chart
            :height="200"
            :data="mainChartTotalDisplay(statData.answers)"
            :printable="printCharts"
            :tooltipsEnabled="!printCharts"
            :animationDuration="1"
            v-if="statData.chart === 'DONUT'"
          />
          <stacked-bar-chart
            :height="300"
            :printable="printCharts"
            :tooltipsEnabled="!printCharts"
            :animationDuration="1"
            :showLegend="true"
            :legendPosition="'bottom'"
            :colorCycle="[generateTitleColor(statData.question, [260, 290])]"
            :data="stackedTotalDisplay(statData.answers)"
            :options="stackedChartOptions"
            v-if="statData.chart === 'VERTICAL_BAR_STACKED'"
          />
          <cloud
            v-if="statData.chart === 'WORDCLOUD'"
            :data="statData.words || []"
            :fontSizeMapper="fontSizeMapper"
            :width="370"
            :height="300"
            :padding="5"
          />
        </div>
        <div>
          <div class="row" v-if="questionLegend && statData.chart != 'VERTICAL_BAR_STACKED'">
            <div class="col-md-6 col-md-offset-3">
              <chart-legend
                :titleAlign="'left'"
                :data="mainChartTotalDisplay(statData.answers)"
                :isPercentage="selectedTotalDisplay === 'percentage'"
              />
            </div>
          </div>

          <stacked-chart-legend
            :data="mainChartTotalDisplay(statData.answers)"
            :isPercentage="selectedTotalDisplay === 'percentage'"
            v-else-if="questionLegend"
          />

          <div class="monitor toggle text-right">
            <a @click="questionLegend = false" v-if="questionLegend">[Hide legend]</a>
            <a @click="questionLegend = true" v-if="!questionLegend">[Show legend]</a>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-xs-12">
          <div class="monitor">
            <el-tooltip class="item" content="Toggle page break" placement="right">
              <el-button
                type="text"
                class="pull-right"
                @click="pageBreaks.demographics = !pageBreaks.demographics"
              >
                <i class="fa fa-fw fa-ellipsis-h"></i>
              </el-button>
            </el-tooltip>
          </div>
          <div class="page-break" v-if="pageBreaks.demographics"></div>
          <div class="monitor clearfix"></div>

          <div class="monitor pull-right">
            <el-tooltip class="item" content="Toggle bullet points" placement="right">
              <el-button
                type="text"
                class="pull-right"
                @click="handleToggleBulletPoints('demographics')"
              >
                <i class="fa fa-fw fa-circle"></i>
              </el-button>
            </el-tooltip>
          </div>
          <div class="bullet-points" v-if="bulletPointsCollection.hasBulletPoints['demographics']">
            <ul>
              <li v-for="(bulletPoint, index) in bulletPointsCollection.demographics" :key="index">
                <div class="monitor">
                  <span class="pull-left">
                    <el-input
                      class="bullet-edit"
                      v-model="bulletPointsCollection.demographics[index]"
                    ></el-input>
                  </span>
                </div>
                <div class="print">{{ bulletPoint }}</div>
                <span class="monitor">
                  <el-button type="text" @click="handleRemoveBulletPoint(index, 'demographics')">
                    <i class="fa fa-fw fa-times"></i>
                  </el-button>
                </span>
                <span class="clearfix"></span>
              </li>
            </ul>

            <div class="monitor">
              <el-form class="bullet-form" :inline="true" @submit.native.prevent>
                <el-form-item>
                  <el-input
                    class="bullet-edit"
                    v-model="newBulletPoint.demographics"
                    @keyup.enter.native="handleAddBulletPoint('demographics')"
                  ></el-input>
                </el-form-item>
                <el-form-item>
                  <el-button type="primary" @click="handleAddBulletPoint('demographics')"
                    >Add</el-button
                  >
                </el-form-item>
              </el-form>
            </div>
          </div>
          <div class="clearfix"></div>
        </div>
      </div>

      <!-- statrt demographics -->

      <!-- statrt demographics -->

      <div class="demographics row">
        <div v-for="(selectedAnalytic, index) in selectedAnalytics" :key="index" class="col-xs-4">
          <div class="monitor selector">
            <div>
              <el-select
                v-model="selectedAnalytic.key"
                filterable
                clearable
                placeholder="Select one"
                @change="handleQuestionChanged(index, selectedAnalytic.key)"
              >
                <el-option-group
                  v-for="group in selectableAnalytics"
                  :key="group.label"
                  :label="group.label"
                >
                  <el-option
                    v-for="item in group.options"
                    :key="`${group.key}__${item.value}`"
                    :label="item.label"
                    :value="`${group.key}__${item.value}`"
                  >
                    <span style="float: left">{{ item.label }}</span>
                    <span style="float: right" v-if="item.type === 'SCALE'" @click.stop>
                      <strong class="positive-only-check-box">Positive</strong>
                      <el-checkbox
                        v-model="positiveSelections[index][`${group.key}__${item.value}`]"
                      ></el-checkbox>
                    </span>
                  </el-option>
                </el-option-group>
              </el-select>
            </div>
            <div>
              <el-select v-model="selectedAnalytic.chart" placeholder="Chart">
                <el-option
                  v-for="item in selectableCharts"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                ></el-option>
              </el-select>
            </div>
          </div>

          <div class="chart-title">
            <h4 v-if="!isEditingQuestion[index]">
              {{ editedQuestions[index] || selectedTitleDisplay(selectedAnalytic.key) }}
            </h4>
            <div
              class="monitor pull-right"
              v-if="
                !isEditingQuestion[index] &&
                  (editedQuestions[index] || selectedTitleDisplay(selectedAnalytic.key))
              "
            >
              <el-button type="text" @click="handleEditQuestion(index)">
                <i class="fa fa-fw fa-edit"></i>
              </el-button>
            </div>
            <div v-if="isEditingQuestion[index]">
              <div class="monitor">
                <textarea v-model="editedQuestions[index]"></textarea>
                <div class="insight-variables">
                  <el-button-group>
                    <el-button
                      type="danger"
                      size="mini"
                      @click="handleResetQuestion(index, selectedAnalytic.key)"
                      >Reset</el-button
                    >
                    <el-button
                      v-if="isEditingQuestion[index]"
                      type="primary"
                      size="mini"
                      @click="handleSaveQuestion(index)"
                      >Save</el-button
                    >
                  </el-button-group>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div class="chart-wrapper" v-if="selectedAnalytic.key">
              <horizontal-bar-chart
                :height="150"
                :data="displayKeyData(index, selectedAnalytic.key)"
                :printable="printCharts"
                :tooltipsEnabled="!printCharts"
                :showYAxes="false"
                :animationDuration="1"
                v-if="selectedAnalytic.chart === 'horizontal_bar'"
              ></horizontal-bar-chart>
              <bar-chart
                :height="150"
                :data="displayKeyData(index, selectedAnalytic.key)"
                :printable="printCharts"
                :tooltipsEnabled="!printCharts"
                :showXAxes="false"
                :animationDuration="1"
                v-if="selectedAnalytic.chart === 'vertical_bar'"
              ></bar-chart>
              <pie-chart
                :height="150"
                :data="displayKeyData(index, selectedAnalytic.key)"
                :printable="printCharts"
                :tooltipsEnabled="!printCharts"
                :animationDuration="1"
                v-if="selectedAnalytic.chart === 'pie'"
              ></pie-chart>
              <doughnut-chart
                :height="150"
                :data="displayKeyData(index, selectedAnalytic.key)"
                :printable="printCharts"
                :tooltipsEnabled="!printCharts"
                :animationDuration="1"
                v-if="selectedAnalytic.chart === 'donut'"
              ></doughnut-chart>
              <cloud
                v-if="selectedAnalytic.chart === 'word_cloud'"
                :data="displayWordData(selectedAnalytic.key)"
                :fontSizeMapper="fontSizeMapper"
                :width="170"
                :height="350"
                :padding="5"
              />
              <stacked-bar-chart
                :height="300"
                :printable="printCharts"
                :tooltipsEnabled="!printCharts"
                :animationDuration="1"
                :showLegend="selectedAnalytic.showLegend"
                :legendPosition="'bottom'"
                :data="displayKeyData(index, selectedAnalytic.key, selectedAnalytic.chart)"
                :options="stackedChartOptions"
                v-if="selectedAnalytic.chart === 'vertical_bar_stacked'"
              />
              <chart-legend
                :data="displayKeyData(index, selectedAnalytic.key)"
                :isPercentage="selectedTotalDisplay === 'percentage'"
                v-if="
                  selectedAnalytic.showLegend && selectedAnalytic.chart != 'vertical_bar_stacked'
                "
              />
              <div class="monitor toggle text-right" v-if="selectedAnalytic.key">
                <a @click="selectedAnalytic.showLegend = false" v-if="selectedAnalytic.showLegend"
                  >[Hide legend]</a
                >
                <a @click="selectedAnalytic.showLegend = true" v-if="!selectedAnalytic.showLegend"
                  >[Show legend]</a
                >
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-xs-12">
          <div class="monitor">
            <el-tooltip class="item" content="Toggle page break" placement="right">
              <el-button
                type="text"
                class="pull-right"
                @click="pageBreaks.sourcing = !pageBreaks.sourcing"
              >
                <i class="fa fa-fw fa-ellipsis-h"></i>
              </el-button>
            </el-tooltip>
          </div>
          <div class="page-break" v-if="pageBreaks.sourcing"></div>
          <div class="monitor clearfix"></div>

          <!-- start of custom text box -->
          <div class="insight" v-if="true">
            <div>{{ textBoxContext }}</div>
            <div class="monitor pull-right" v-if="!isEditingTextBox">
              <el-tooltip class="item" content="Edit text" placement="right">
                <el-button type="text" @click="isEditingTextBox = true">
                  <i class="fa fa-fw fa-edit"></i>
                </el-button>
              </el-tooltip>
            </div>
            <div v-else>
              <div class="monitor">
                <textarea v-model="textBoxContext" ref="customTextArea"></textarea>
                <div class="edit-buttons">
                    <el-button type="danger" size="mini" @click="handleResetCustomText"
                      >Reset</el-button
                    >
                    <el-button
                      v-if="isEditingTextBox"
                      type="primary"
                      size="mini"
                      @click="handleSaveCustomText"
                      >Save</el-button
                    >
                </div>
              </div>
            </div>
            <div class="monitor clearfix"></div>
          </div>
          <!-- end of custom text box  -->

          <div class="monitor pull-right">
            <el-tooltip class="item" content="Toggle bullet points" placement="right">
              <el-button
                type="text"
                class="pull-right"
                @click="handleToggleBulletPoints('sourcing')"
              >
                <i class="fa fa-fw fa-circle"></i>
              </el-button>
            </el-tooltip>
          </div>
          <div class="bullet-points" v-if="bulletPointsCollection.hasBulletPoints['sourcing']">
            <ul>
              <li v-for="(bulletPoint, index) in bulletPointsCollection.sourcing" :key="index">
                <div class="monitor">
                  <span class="pull-left">
                    <el-input
                      class="bullet-edit"
                      v-model="bulletPointsCollection.sourcing[index]"
                    ></el-input>
                  </span>
                </div>
                <div class="print">{{ bulletPoint }}</div>
                <span class="monitor">
                  <el-button type="text" @click="handleRemoveBulletPoint(index, 'sourcing')">
                    <i class="fa fa-fw fa-times"></i>
                  </el-button>
                </span>
                <span class="clearfix"></span>
              </li>
            </ul>
            <div class="monitor">
              <el-form class="bullet-form" :inline="true" @submit.native.prevent>
                <el-form-item>
                  <el-input
                    class="bullet-edit"
                    v-model="newBulletPoint.sourcing"
                    @keyup.enter.native="handleAddBulletPoint('sourcing')"
                  ></el-input>
                </el-form-item>
                <el-form-item>
                  <el-button type="primary" @click="handleAddBulletPoint('sourcing')"
                    >Add</el-button
                  >
                </el-form-item>
              </el-form>
            </div>
          </div>

          <div class="clearfix"></div>
        </div>
      </div>

      <div class="row">
        <div class="col-xs-12 sourcing">
          <div class="monitor">
            <label>Extra notes / sourcing information: &nbsp;</label>
            <el-button
              size="mini"
              type="primary"
              class="pull-right"
              icon="el-icon-close"
              @click="toggleSaveNotes()"
              >{{ isEditSourcing ? 'Cancel' : 'Edit' }} Notes</el-button
            >

            <el-button
              size="mini"
              type="primary"
              class="pull-right"
              v-if="isEditSourcing"
              icon="el-icon-check"
              @click="handleSaveSourceNotes(sourcing)"
              >Save Notes</el-button
            >

            <el-input
              v-if="isEditSourcing"
              type="textarea"
              v-model="sourcing"
              :autosize="{ minRows: 2, maxRows: 6 }"
            ></el-input>
            <div class="well well-sm" v-if="!isEditSourcing">{{ sourcing }}</div>
          </div>
        </div>

        <div class="col-xs-12 print">
          <!-- <div class="page-break" v-if="pageBreaks.sourcing"></div> -->
          <div v-html="formatSourcing(sourcing)"></div>
        </div>
      </div>
      <!-- end demographics -->
    </div>
    <span slot="footer">
      <el-button @click="print()">Print</el-button>
    </span>
  </el-dialog>
</template>

<script>
import axios from 'axios';
import sanitizeHtml from 'sanitize-html';
import colorMixin from '@/mixins/colors';
import { upperFirst } from 'lodash';
import { Printd } from 'printd';
import { mapState } from 'vuex';
// import moment from 'moment';
import gql from 'graphql-tag';
import PieChart from '@/components/charts/PieChart';
import BarChart from '@/components/charts/BarChart';
import DoughnutChart from '@/components/charts/DoughnutChart';
import HorizontalBarChart from '@/components/charts/HorizontalBarChart';
import ChartLegend from '@/components/charts/ChartLegend';
import StackedChartLegend from '@/components/charts/StackedChartLegend';
import StackedBarChart from '@/components/charts/StackedBarChart';
import Cloud from 'vue-d3-cloud';

export default {
  components: {
    PieChart,
    BarChart,
    DoughnutChart,
    HorizontalBarChart,
    ChartLegend,
    StackedChartLegend,
    Cloud,
    StackedBarChart,
  },
  mixins: [colorMixin],
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: true,
    },
    statData: {
      type: Object,
      default: () => ({
        insight: '',
        answers: [],
        words: [],
        demographics: {},
      }),
    },
    selectedTotalDisplay: {
      type: String,
      default: 'percentage',
    },
  },
  data: () => ({
    selectedAnalytics: [
      { chart: 'pie', key: null, showLegend: true },
      { chart: 'pie', key: null, showLegend: true },
      { chart: 'pie', key: null, showLegend: true },
    ],
    isEditingTitle: false,
    isEditingInsight: false,
    isEditingTextBox: false,
    isEditSourcing: false,
    printCharts: true,
    textBoxContext: '',
    insightVariables: [
      { key: 'positive_percentage', title: 'Positive by percentage' },
      { key: 'positive_cume', title: 'Positive adjusted by cume' },
      { key: 'positive_total', title: 'Total positive' },
    ],
    customInsight: null,
    customTitle: null,
    colonyLogoB64: null,
    companyLogoB64: null,
    editedQuestions: [null, null, null],
    isEditingQuestion: [false, false, false],
    pageBreaks: { demographics: false, sourcing: false },

    questionLegend: true,
    newBulletPoint: { demographics: '', sourcing: '' },

    bulletPointsCollection: {
      hasBulletPoints: {
        demographics: false,
        sourcing: false,
        bullet: false,
      },
      demographics: [],
      sourcing: [],
      bullet: [],
    },

    sourcingNotes: null,

    positiveSelections: { main: false, 0: {}, 1: {}, 2: {} },

    printableCss: `
    /* Print CSS Starts here */
      body, html {
        -webkit-print-color-adjust: exact;
      }
      img {
        width: auto;
        height: auto;
      }
      .one-page {
        text-align: center;
      }

      .mast-head {
        width: 100%;
        text-align: left;
        border-bottom: 1px solid #eee;
      }

      .mast-head img {
        padding: 10px;
      }

      .logo-wrapper {
        height: 120px;
        display: table-cell;
        vertical-align: middle;
      }

      .colony-logo {
        float: left;
        max-width: 300px;
      }

      .colony-logo img {
        height: 70px;
      }

      .company-logo {
        float: right;
        max-width: 300px;
      }

      .company-logo img {
        max-height: 120px;
        width: auto;
      }

      .insight {
        padding: 20px 0;
        font-size: 18px;
        line-height: 1.1;
      }
      .monitor {
        display: none;
      }
      .selector {
        margin-bottom: 15px;
      }
      .legend {
        margin: auto;
        border-collapse: collapse;
        display: table;
        padding: 12px 0;
      }
      .legend-row {
        display: table-row;
      }
      .column {
        display: table-cell;
        padding: 0px 10px;
      }
      .color {
        display: inline-block;
        width: 10px;
        height: 10px;
      }
      .total {
        text-align: right;
      }
      .demographics .chart-title {
        height: 120px;
      }
      .sourcing {
        color: #999;
        font-size: 0.8em;
      }
      .bullet-points {
        text-align: center;
        font-size: 1.3em;
        font-weight: bold;
      }
      .bullet-points > ul {
        display: inline-block;
        text-align: left;
      }
      .page-break {
          page-break-after: always;
      }
  `,
  }),
  mounted() {
    this.d = new Printd();
    this.$store.dispatch('company/getCompany');

    this.getDataUrl('https://s3-eu-west-1.amazonaws.com/colonyng/logo/colony-logo-purple.png').then(
      dataUrl => {
        this.colonyLogoB64 = dataUrl;
      },
    );
  },
  apollo: {
    $client: 'apiv2',
    questionSource: {
      query: gql`
        query($filter: QuestionSourcesFilter!) {
          questionSource(filter: $filter) {
            sourceNotes
          }
        }
      `,
      variables() {
        return {
          filter: {
            questionId: this.questionId,
          },
        };
      },
    },
  },
  computed: {
    ...mapState('company', ['company']),
    isVisible: {
      get() {
        this.sourcingNotes = null;
        return this.visible;
      },
      set(value) {
        this.$emit('onChangeVisibility', value);
        if (!value) {
          this.resetData();
        }
      },
    },
    insight: {
      get() {
        return this.customInsight === null ? this.statData.insight : this.customInsight;
      },
      set(value) {
        this.customInsight = value;
      },
    },

    sourcing: {
      get() {
        const defaultFooterText = 'Source: COLONYLive (insert date of survey).';
        const source = this.questionSource ? this.questionSource.sourceNotes : defaultFooterText;
        return this.sourcingNotes === null ? source : this.sourcingNotes;
      },
      set(value) {
        this.sourcingNotes = value;
      },
    },
    displayWordData() {
      return k => {
        if (!k) {
          return [];
        }

        const answersMap = {
          otherQuestions: this.otherQuestionWords,
        };
        const keys = k.split('__');
        const answers = answersMap[keys[0]](keys[1]);
        return answers;
      };
    },

    displayKeyData() {
      return (index, key, chart) => {
        if (!key) {
          return [];
        }

        const answersMap = {
          otherQuestions: this.otherQuestionAnswers,
          demographics: this.demographicAnswers,
        };

        const keys = key.split('__');
        const answers = answersMap[keys[0]](keys[1]);

        if (chart === 'vertical_bar_stacked') return this.stackedTotalDisplay(answers);

        const isPositiveOnly = this.positiveSelections[index][key] || false;

        return isPositiveOnly ? this.totalDisplayPositive(answers) : this.totalDisplay(answers);
      };
    },

    mainChartTotalDisplay() {
      return answers => {
        const isPositiveOnly = this.positiveSelections.main || false;
        return isPositiveOnly ? this.totalDisplayPositive(answers) : this.totalDisplay(answers);
      };
    },

    selectedTitleDisplay() {
      return k => {
        if (!k) {
          return '';
        }

        const titleMap = {
          otherQuestions: this.otherQuestionTitle,
          demographics: this.demographicTitle,
        };

        const keys = k.split('__');
        return titleMap[keys[0]](keys[1]);
      };
    },

    selectableAnalytics() {
      return [
        {
          label: 'Questions',
          key: 'otherQuestions',
          options: this.otherQuestionOptions(),
        },
        {
          label: 'Demographics',
          key: 'demographics',
          options: this.demographicChartOptions(),
        },
      ];
    },
    selectableCharts() {
      const chartTypes = [
        'vertical_bar_stacked',
        'vertical_bar',
        'horizontal_bar',
        'pie',
        'donut',
        'word_cloud',
      ];
      return chartTypes.map(c => ({ value: c, label: this.titleDisplay(c) }));
    },

    processedInsight() {
      const positiveAnswers = () => this.statData.answers.filter(a => a.polarity > 0);
      const substitutions = {
        positive_percentage: () => {
          const total = positiveAnswers().reduce((acc, a) => acc + a.percentage, 0);
          return `${total}%`;
        },
        positive_cume: () =>
          positiveAnswers()
            .reduce((acc, a) => acc + a.cume, 0)
            .toLocaleString(),
        positive_total: () =>
          positiveAnswers()
            .reduce((acc, a) => acc + a.total, 0)
            .toLocaleString(),
      };
      const re = /{([^}]+)}/g;

      const found = this.insight.match(re);

      if (!found) {
        return this.insight;
      }

      let newInsight = this.insight;
      found.forEach(f => {
        const k = f.replace(/[{}]/g, '');
        if (Object.keys(substitutions).indexOf(k) >= 0) {
          newInsight = newInsight.replace(
            new RegExp(f, 'g'),
            `<strong>${substitutions[k]()}</strong>`,
          );
        }
      });

      // Convert new lines into br's
      newInsight = newInsight.replace(/(?:\r\n|\r|\n)/g, '<br>');

      return newInsight;
    },

    currentTitle: {
      get() {
        return this.customTitle !== null ? this.customTitle : this.title;
      },

      set(value) {
        this.customTitle = value;
      },
    },

    questionId() {
      return this.statData.questionId;
    },

    filterVariables() {
      const keyMap = {
        questionId: this.statData.questionId,
        sourceNotes: this.sourcingNotes,
      };
      return keyMap;
    },
    stackedChartOptions() {
      return {
        tooltips: {
          callbacks: {
            label: this.generateTooltipLabel,
          },
        },
        scales: {
          xAxes: [
            {
              stacked: true,
              barPercentage: 1,
            },
          ],
          yAxes: [
            {
              stacked: true,
            },
          ],
        },
      };
    },
  },

  methods: {
    getDataUrl(url) {
      return fetch(url)
        .then(response => response.blob())
        .then(
          blob =>
            new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            }),
        );
    },

    print() {
      axios
        .get('https://maxcdn.bootstrapcdn.com/bootstrap/3.3.7/css/bootstrap.min.css')
        .then(response => {
          const bootstrapCss = response.data;
          this.d.print(this.$refs.print, bootstrapCss + this.printableCss);
        });
    },
    totalDisplay(answers) {
      return answers ? answers.map(a => [a.title, a[this.selectedTotalDisplay]]) : [];
    },
    totalDisplayPositive(answers) {
      const positiveAnswers = answers.filter(a => a.polarity > 0);
      return this.totalDisplay(positiveAnswers);
    },
    stackedTotalDisplay(answers) {
      /* eslint-disable-next-line */
      const result = {
        labels: answers.map(a => a.title),
        datasets: answers.map((a, i) => ({
          backgroundColor: this.generateTitleColor(`Ranked # ${i + 1}`),
          label: `Ranked  #${i + 1}`,
          data: answers.map(d => {
            if (d[this.selectedTotalDisplay][i] === undefined) return 0;
            return d[this.selectedTotalDisplay][i];
          }),
        })),
      };
      return result;
    },
    titleDisplay(str) {
      return upperFirst(str).replace(/_/g, ' ');
    },
    otherQuestionTitle(key) {
      return this.selectableAnalytics
        .find(a => a.key === 'otherQuestions')
        .options.find(o => o.value === parseInt(key, 10)).label;
    },
    otherQuestionAnswers(key) {
      return this.statData.otherQuestions.find(q => q.question_id === parseInt(key, 10)).answers;
    },
    otherQuestionWords(key) {
      return this.statData.otherQuestions.find(q => q.question_id === parseInt(key, 10)).words;
    },
    otherQuestionOptions() {
      return this.statData.otherQuestions.map(q => ({
        value: q.question_id,
        label: q.question,
        type: q.question_type,
      }));
    },
    demographicTitle(key) {
      return this.titleDisplay(key);
    },
    demographicAnswers(key) {
      return this.statData.demographics[key];
    },
    demographicChartOptions() {
      return Object.keys(this.statData.demographics).map(k => ({
        value: k,
        label: this.titleDisplay(k),
      }));
    },
    handleAppendInsightVariable(ref, variable) {
      const cursorPosition = this.$refs[ref].selectionStart;
      this.insight = [
        this.insight.slice(0, cursorPosition),
        `{${variable}}`,
        this.insight.slice(cursorPosition),
      ].join('');
    },
    handleResetInsight() {
      this.customInsight = this.statData.insight;
    },
    handleSaveInsight() {
      this.isEditingInsight = false;
    },
    handleSaveCustomText() {
      this.isEditingTextBox = false;
    },
    handleResetCustomText() {
      this.textBoxContext = '';
    },
    handleQuestionChanged(index, key) {
      this.editedQuestions[index] = this.selectedTitleDisplay(key);
    },
    handleEditQuestion(index) {
      this.isEditingQuestion[index] = true;
      this.$forceUpdate();
    },
    handleSaveQuestion(index) {
      this.isEditingQuestion[index] = false;
      this.$forceUpdate();
    },
    handleResetQuestion(index, key) {
      this.isEditingQuestion[index] = false;
      this.editedQuestions[index] = this.selectedTitleDisplay(key);
      this.$forceUpdate();
    },
    handleResetTitle() {
      this.customTitle = null;
    },

    handleSaveTitle() {
      this.isEditingTitle = false;
    },
    handleAddBulletPoint(indexName = 'insight') {
      this.bulletPointsCollection[indexName].push(this.newBulletPoint[indexName]);
      this.newBulletPoint[indexName] = '';
    },
    handleToggleBulletPoints(indexName = 'insight') {
      this.bulletPointsCollection.hasBulletPoints[indexName] = !this.bulletPointsCollection
        .hasBulletPoints[indexName];
    },
    handleRemoveBulletPoint(index, indexName = 'insight') {
      this.bulletPointsCollection[indexName].splice(index, 1);
    },
    toggleSaveNotes() {
      this.isEditSourcing = !this.isEditSourcing;
    },
    handleSaveSourceNotes() {
      this.saveToExternal();
      this.toggleSaveNotes();
      this.$forceUpdate();
    },
    saveToExternal() {
      this.$apollo
        .mutate({
          client: 'apiv2',
          mutation: gql`
            mutation($filter: QuestionSourcesFilter!) {
              addSourceNotes(filter: $filter) {
                questionId
                sourceNotes
              }
            }
          `,
          variables: {
            filter: this.filterVariables,
          },
        })
        .then(data => {
          const { sourceNotes } = data;
          this.isEditSourcing = false;
          this.sourcingNotes = this.nl2br(sourceNotes);
        });
    },
    formatSourcing(sourcing) {
      return sanitizeHtml(this.nl2br(sourcing));
    },
    nl2br(str) {
      return str.replace(/(?:\r\n|\r|\n)/g, '<br />');
    },
    resetData() {
      const data = {
        selectedAnalytics: [
          { chart: 'pie', key: null, showLegend: true },
          { chart: 'pie', key: null, showLegend: true },
          { chart: 'pie', key: null, showLegend: true },
        ],
        isEditingTitle: false,
        isEditingInsight: false,
        isEditSourcing: false,
        printCharts: true,
        insightVariables: [
          { key: 'positive_percentage', title: 'Positive by percentage' },
          { key: 'positive_cume', title: 'Positive adjusted by cume' },
          { key: 'positive_total', title: 'Total positive' },
        ],
        customInsight: null,
        customTitle: null,
        editedQuestions: [null, null, null],
        isEditingQuestion: [false, false, false],
        pageBreaks: { demographics: false, sourcing: false },

        questionLegend: true,
        newBulletPoint: { demographics: '', sourcing: '' },

        bulletPointsCollection: {
          hasBulletPoints: {
            demographics: false,
            sourcing: false,
            bullet: false,
          },
          demographics: [],
          sourcing: [],
          bullet: [],
        },

        sourcingNotes: null,
      };
      Object.keys(data).forEach(k => {
        this[k] = data[k];
      });
    },
    fontSizeMapper: word => Math.log2(word.value) * 20,
  },
  watch: {
    company() {
      if (this.company.logo) {
        this.getDataUrl(this.company.logo).then(dataUrl => {
          this.companyLogoB64 = dataUrl;
        });
      }
    },
  },
};
</script>

<style lang="css" scoped>
.one-page {
  text-align: center;
}

.well {
  margin-top: 10px;
  min-height: 40px;
}
.mast-head {
  width: 100%;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.mast-head img {
  padding: 10px;
}

.logo-wrapper {
  height: 120px;
  display: table-cell;
  vertical-align: middle;
}

.colony-logo {
  float: left;
  max-width: 300px;
}

.colony-logo img {
  height: 70px;
}

.company-logo {
  float: right;
  max-width: 300px;
}

.company-logo img {
  max-height: 120px;
  width: auto;
}

.insight {
  padding: 20px 0;
  font-size: 22px;
  line-height: 1.1;
}

.insight textarea {
  width: 100%;
  padding: 8px;
  font-size: 0.7em;
  margin-top: 15px;
}

.edit-buttons {
  text-align: right;
}

.chart-title textarea {
  width: 100%;
}

.demographics .chart-title {
  height: 120px;
}

.sourcing {
  padding: 15px;
  text-align: left;
}

.print {
  display: none;
}

.toggle {
  font-size: 0.7em;
}

.selector {
  margin-bottom: 15px;
}

.bullet-points {
  text-align: left;
  width: 95%;
}

.bullet-form {
  padding-left: 40px;
}

.bullet-edit {
  width: 400px !important;
}

.page-break {
  border-bottom: 2px dashed #ddd;
  margin: 5px 0;
}

.positive-only {
  font-size: 0.7em;
  color: #ccc;
}
.positive-only-check-box {
  font-size: 0.7em;
  color: #585858;
}
</style>



// WEBPACK FOOTER //
// CategoryOneSheet.vue?7f52af62